/**
 * Контекстное меню строки чата (правый клик).
 *
 * ПОЧЕМУ ПРАВЫЙ КЛИК, А НЕ КНОПКА «⋯» В СТРОКЕ. Строка чата имеет
 * ФИКСИРОВАННУЮ высоту 60px и уже занята аватаром, именем, временем, превью и
 * двумя бейджами (см. комментарий в RoomList.tsx). Кнопка в ней либо съела бы
 * ширину превью, либо появлялась бы только на hover — а тогда её нет на
 * тач-устройствах. Правый клик — привычный жест и в Telegram, и в Element,
 * и ничего не отнимает у строки.
 *
 * ПОЗИЦИОНИРОВАНИЕ. position: fixed по координатам курсора, со сдвигом внутрь
 * окна, если меню не влезает у нижнего/правого края. Без сдвига пункты у
 * нижней комнаты списка оказались бы за границей экрана.
 *
 * ДИЗАЙН ЧЕРНОВОЙ: прямоугольник, рамка, строки без иконок.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useT } from "../i18n";

import type { RoomSummary } from "../matrix/rooms";
import { s } from "./styles";
import { color } from "./theme";

export interface RoomMenuTarget {
  room: RoomSummary;
  x: number;
  y: number;
}

export function RoomMenu({
  target,
  onClose,
  onToggleFavourite,
  onInvite,
  onRoomLink,
  onLeave,
  onAcceptInvite,
  onDeclineInvite,
  onSettings,
  onMoveToSpace,
}: {
  target: RoomMenuTarget;
  onClose: () => void;
  onToggleFavourite: (room: RoomSummary) => void;
  onInvite: (room: RoomSummary) => void;
  /** «Ссылка на комнату…» (Антон 16.10). Опционально — старые вызовы компилируются. */
  onRoomLink?: (room: RoomSummary) => void;
  onLeave: (room: RoomSummary) => void;
  onAcceptInvite: (room: RoomSummary) => void;
  onDeclineInvite: (room: RoomSummary) => void;
  /** Этап 2: открыть настройки комнаты. Опционально — старые вызовы без него компилируются. */
  onSettings?: (room: RoomSummary) => void;
  /**
   * «Перенести в пространство…». Передаётся ТОЛЬКО когда у меня есть права
   * на m.space.child хотя бы в одном подходящем пространстве (проверяет
   * вызывающий код через mayManageSpaceChild — клиент не предлагает
   * недопустимое, модель доступа п.3/4d). Нет прав — пункта нет вовсе:
   * защита всё равно серверная, а пустая кнопка выглядела бы поломкой.
   */
  onMoveToSpace?: (room: RoomSummary) => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y });
  const { room } = target;

  /**
   * Закрытие по клику мимо, по второму правому клику и по Esc. Все три: меню,
   * которое нельзя закрыть привычным способом, воспринимается как зависшее.
   *
   * ДЕФЕКТ, НАЙДЕННЫЙ ЖИВЫМ ПРОГОНОМ (шаг 5 первого запуска, меню в DOM = 0):
   * если повесить слушателей ПРЯМО в эффекте, меню закрывает само себя тем
   * же событием, которое его открыло. Причина в порядке: React слушает на корневом
   * контейнере, contextmenu — дискретное событие, поэтому setState флашится
   * СИНХРОННО ещё внутри диспетчеризации: компонент монтируется, эффект вешает
   * слушателя на window — и ТОЛЬКО ПОТОМ тот же нативный contextmenu всплывает
   * до window и зовёт close(). Поэтому подписка откладывается на следующий тик:
   * к этому моменту открывающее событие уже отработано целиком.
   */
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const id = setTimeout(() => {
      window.addEventListener("mousedown", close);
      window.addEventListener("contextmenu", close);
      window.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("mousedown", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(target.x, window.innerWidth - r.width - 8);
    const top = Math.min(target.y, window.innerHeight - r.height - 8);
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [target.x, target.y]);

  const item = (label: string, testId: string, fn: () => void, danger = false) => (
    <button
      type="button"
      style={{ ...s.menuItem, color: danger ? color.danger : color.text }}
      data-testid={testId}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        fn();
        onClose();
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      style={{ ...s.menu, left: pos.left, top: pos.top }}
      data-testid="room-menu"
      data-room-id={room.roomId}
      onContextMenu={(e) => e.preventDefault()}
    >
      {room.invite ? (
        <>
          {item(t("room.acceptInviteMenu"), "menu-accept", () => onAcceptInvite(room))}
          {item(t("room.declineInviteMenu"), "menu-decline", () => onDeclineInvite(room), true)}
        </>
      ) : (
        <>
          {room.pinned
            ? item(t("room.unfavouriteMenu"), "menu-unfavourite", () => onToggleFavourite(room))
            : item(t("room.favouriteMenu"), "menu-favourite", () => onToggleFavourite(room))}
          {item(t("room.inviteMenu"), "menu-invite", () => onInvite(room))}
          {onRoomLink && item(t("room.linkMenu"), "menu-room-link", () => onRoomLink(room))}
          {/* Перенос — ТОЛЬКО когда передали обработчик (= есть права хотя
              бы в одном целевом пространстве). См. комментарий у пропа. */}
          {onMoveToSpace &&
            item(t("ui.perenesti_v_prostranstvo"), "menu-move-to-space", () =>
              onMoveToSpace(room),
            )}
          {onSettings && item(t("ui.nastroyki_komnaty"), "menu-settings", () => onSettings(room))}
          {item(t("room.leaveMenu"), "menu-leave", () => onLeave(room), true)}
        </>
      )}
    </div>
  );
}
